import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext } from 'react';
import { useMutateUserRolePreferences } from '../api/userrolepreferences/hooks';
import { UserRolePreferencesSettings } from '../api/userrolepreferences/types';
import { Filter } from '../common/components/filter/filterbar/types';
import {
  UserRolePreferencesServiceContext,
  useUserRolePreferencesServiceContext,
  useUserRolePreferencesSettingsContext,
} from '../context/contexts';
import { TourKey } from './tours';

/**
 * Read and persist whether the current role has seen a given tour.
 * Prefs are loaded with the dashboard; markSeen saves seenTours to the backend.
 */
export const useSeenTour = (tourKey: TourKey) => {
  const prefs = useUserRolePreferencesSettingsContext();
  const seen = prefs?.settings?.seenTours?.[tourKey] === true;
  const { mutate } = useMutateUserRolePreferences<Filter>();
  const service = useUserRolePreferencesServiceContext<Filter>();
  const queryClient = useQueryClient();

  const markSeen = useCallback(() => {
    if (seen) return;

    const base = prefs?.settings ?? { myFilterGroups: [], profile: {} };
    const nextSettings = {
      ...base,
      seenTours: { ...(base.seenTours ?? {}), [tourKey]: true },
    };

    if (prefs) {
      queryClient.setQueryData<UserRolePreferencesSettings<Filter> | null>(
        ['userRolePreferences', ...service.getDeps()],
        { ...prefs, settings: nextSettings }
      );
    }
    mutate(nextSettings);
  }, [seen, prefs, service, queryClient, mutate, tourKey]);

  return { seen, markSeen };
};

/**
 * Like useSeenTour, but usable on pages that can render outside the
 * executor-role provider tree (e.g. the data-upload-only shell, which has no
 * UserRolePreferences provider). When the preferences service is unavailable
 * the tour is reported as seen, so callers simply never start it — showing it
 * on every visit without being able to persist "seen" would be worse.
 */
export const useSeenTourIfAvailable = (tourKey: TourKey) => {
  const prefs = useUserRolePreferencesSettingsContext();
  const service = useContext(UserRolePreferencesServiceContext<Filter>());
  const queryClient = useQueryClient();

  const seen =
    service === null || prefs?.settings?.seenTours?.[tourKey] === true;

  const markSeen = useCallback(() => {
    if (service === null || seen) return;

    const base = prefs?.settings ?? { myFilterGroups: [], profile: {} };
    const nextSettings = {
      ...base,
      seenTours: { ...(base.seenTours ?? {}), [tourKey]: true },
    };

    if (prefs) {
      queryClient.setQueryData<UserRolePreferencesSettings<Filter> | null>(
        ['userRolePreferences', ...service.getDeps()],
        { ...prefs, settings: nextSettings }
      );
    }
    service.save(nextSettings).catch((error: unknown) => {
      // Failing to persist "seen" only means the tour may show again later.
      console.error(error);
    });
  }, [seen, prefs, service, queryClient, tourKey]);

  return { seen, markSeen };
};
